import React, { useRef, useEffect, memo, FormEvent, ChangeEvent } from 'react';
import { Button } from './ui/button';
import { Send, Square, Paperclip, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Attachment {
  name: string;
  url: string;
  contentType?: string;
}

interface PromptInputProps {
  input: string;
  isLoading: boolean;
  onInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onStop?: () => void;
  attachments?: Attachment[];
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  className?: string;
  placeholder?: string;
  maxRows?: number;
}

function PurePromptInput({
  input,
  isLoading,
  onInputChange,
  onSubmit,
  onStop,
  attachments = [],
  onAttachmentsChange,
  className,
  placeholder = 'Type your message...',
  maxRows = 5,
}: PromptInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, maxRows * 24);
    textarea.style.height = `${newHeight}px`;
  }, [input, maxRows]);

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        const form = e.currentTarget.form;
        if (form) {
          form.requestSubmit();
        }
      }
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // In a real implementation, you would upload files here
    // For now, we'll create object URLs for preview
    const newAttachments: Attachment[] = files.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      contentType: file.type,
    }));

    onAttachmentsChange?.([...attachments, ...newAttachments]);

    // Reset file input
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onAttachmentsChange?.(newAttachments);
  };

  const handleSubmitWithAttachments = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(e);
    // Clear attachments after submit
    if (attachments.length > 0) {
      onAttachmentsChange?.([]);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-3">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm"
            >
              <Paperclip className="w-4 h-4 text-muted-foreground" />
              <span className="max-w-[200px] truncate">{attachment.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 hover:bg-background"
                onClick={() => removeAttachment(index)}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmitWithAttachments} className="relative">
        <div className="flex items-end gap-2 p-2 border rounded-2xl bg-background focus-within:ring-2 focus-within:ring-ring">
          {/* Attachment Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={handleAttachmentClick}
            disabled={isLoading}
            title="Attach files"
          >
            <Paperclip className="w-4 h-4" />
          </Button>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
            accept="image/*,.pdf,.doc,.docx,.txt"
          />

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            rows={1}
            className="flex-1 resize-none bg-transparent px-2 py-2 text-sm outline-none disabled:opacity-50 min-h-[36px] max-h-[120px]"
          />

          {/* Submit/Stop Button */}
          {isLoading ? (
            <Button
              type="button"
              size="icon"
              className="h-9 w-9 flex-shrink-0"
              onClick={onStop}
              variant="destructive"
            >
              <Square className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon"
              className="h-9 w-9 flex-shrink-0"
              disabled={!input.trim() && attachments.length === 0}
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>
      </form>

      {/* Helper Text */}
      <p className="text-xs text-muted-foreground text-center px-4">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}

export const PromptInput = memo(
  PurePromptInput,
  (prevProps, nextProps) => {
    // Prevent memoization if key props changed
    if (prevProps.input !== nextProps.input) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.attachments?.length !== nextProps.attachments?.length) return false;
    return true;
  }
);
